import { Fragment, useEffect, useState } from 'react'
import { useFlowBillingRuns } from '../../../hooks/admin/useFlowBilling'
import Pagination from '../Pagination'
import { formatInTimezone, formatVolume } from '../../../utils/formatInTimezone'
import { Card } from './fields'
import { RunActions, StatusPill } from './RunActions'

/**
 * Run history.
 *
 * Every run is listed, including the failed ones — a report that did not go out
 * is exactly what an administrator needs to see, and hiding failures behind a
 * filter default is how a missed billing month goes unnoticed for a year.
 *
 * The stored file stays downloadable whatever the status, because a run that
 * generated and then failed to send still produced the figures.
 */

const DEFAULT_PAGE_SIZE = 10

export function RunHistorySection() {
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE)
  const [status, setStatus] = useState('all')
  const { data, isLoading } = useFlowBillingRuns({ page, limit, status })
  const [expanded, setExpanded] = useState<number | null>(null)

  const runs = data?.runs ?? []

  // Deleting the last run on a page, or narrowing the filter, can leave the
  // table on a page that no longer exists — which reads as "no runs" rather
  // than "wrong page". Step back to the last page that has rows.
  useEffect(() => {
    if (data && page > data.pagination.pages && data.pagination.pages >= 1) {
      setPage(data.pagination.pages)
    }
  }, [data, page])

  return (
    <Card title="Run history">
      <div className="flex items-center gap-3 mb-4">
        <label className="text-sm text-gray-600" htmlFor="run-status">
          Status
        </label>
        <select
          id="run-status"
          className="border border-gray-300 rounded px-3 py-1.5 text-sm"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value)
            setPage(1)
          }}
        >
          <option value="all">All</option>
          <option value="sent">Sent</option>
          <option value="failed">Failed</option>
          <option value="generated">Generated, not sent</option>
          <option value="superseded">Superseded</option>
          <option value="deleted">Deleted</option>
        </select>
      </div>

      {isLoading && <p className="text-sm text-gray-500">Loading runs…</p>}

      {!isLoading && runs.length === 0 && (
        <p className="text-sm text-gray-500 py-3">
          No runs yet. The first appears after the job runs, or after a manual re-run.
        </p>
      )}

      {runs.some((run) => run.status === 'generated') && (
        <p className="text-sm text-gray-600 bg-blue-50 border border-blue-200 rounded p-3 mb-3">
          A <strong>generated</strong> run is yours alone — the group still sees whichever report
          was last published or sent for that period. Publish one to hand it over.
        </p>
      )}

      {runs.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">
                  Period
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">
                  Status
                </th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-600 uppercase">
                  Total
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">
                  Sent
                </th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-600 uppercase">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {runs.map((run) => (
                <Fragment key={run.id}>
                  <tr className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-sm text-gray-900 whitespace-nowrap">
                      {formatInTimezone(run.periodStart, run.timezone).slice(0, 7)}
                      {/* A revision replaces a report somebody already received;
                          a retry is the same report attempted again after a
                          failure. Labelling the second as "rev 3" would tell an
                          administrator a customer had been billed three times. */}
                      {run.revisionOf != null && (
                        <span className="text-xs text-amber-700 ml-1">rev {run.attempts}</span>
                      )}
                      {run.revisionOf == null && run.attempts > 1 && (
                        <span className="text-xs text-gray-500 ml-1">attempt {run.attempts}</span>
                      )}
                      {/* Why a period was re-run belongs beside the run, not
                          only in the email the recipients got. */}
                      {run.revisionOf != null && run.reason && (
                        <div className="text-xs text-gray-500 font-normal max-w-xs truncate" title={run.reason}>
                          {run.reason}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <StatusPill status={run.status} />
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-900 text-right whitespace-nowrap">
                      {formatVolume(run.totalVolume, run.decimalPlaces)}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap">
                      {run.sentAt ? formatInTimezone(run.sentAt, run.timezone) : '—'}
                    </td>
                    <td className="px-3 py-2 text-sm text-right whitespace-nowrap">
                      <RunActions
                        run={run}
                        errorShown={expanded === run.id}
                        onToggleError={() => setExpanded(expanded === run.id ? null : run.id)}
                      />
                    </td>
                  </tr>
                  {expanded === run.id && run.error && (
                    <tr>
                      <td colSpan={5} className="px-3 pb-3">
                        <div className="bg-red-50 border border-red-200 rounded p-3 text-xs text-red-800 font-mono break-all">
                          {run.error}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Attempt {run.attempts} of 3. The generated file is kept and stays
                          downloadable.
                        </p>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>

          {/* Shown whenever there are runs at all, not only past the first
              page: the row count is the answer to "did that generate?" and
              hiding it until there are eleven runs is hiding it exactly when a
              district is being set up. */}
          {data && (
            <Pagination
              currentPage={data.pagination.page}
              totalPages={data.pagination.pages}
              totalItems={data.pagination.total}
              pageSize={limit}
              onPageSizeChange={(size) => {
                setLimit(size)
                setPage(1)
              }}
              onPageChange={setPage}
            />
          )}
        </div>
      )}
    </Card>
  )
}
